/**
 * Component Upgrade Dialog
 * Shows all component slots for a unit, aligned with the store categories.
 * Includes virtual "built-in" defaults (e.g., Mk0 Mining Array) and CTAs
 * to open the component marketplace for upgrades.
 */

import { EventBus } from '../../utils/EventBus';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { ComponentInstance, ComponentDefinition } from '../../shared/common/MonetizationTypes';
import {
  createDialogChrome,
  Colors, CSSColors, Fonts, FontSize, Space, Radius,
} from './GameUITheme';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';
const SLOT_H = 68;
const SLOT_GAP = 4;
const SLOT_PAD = Space.lg;

const MINERAL_LABELS: Record<string, string> = {
  min_iron: 'Iron', min_copper: 'Copper', min_titanium: 'Titanium',
  min_platinum: 'Platinum', min_rhodium: 'Rhodium', min_palladium: 'Palladium',
  min_iridium: 'Iridium', min_darkmat: 'Dark Matter',
};

interface SlotDef {
  category: string;
  label: string;
  icon: string;
  desc: string;
  builtIn?: { name: string; tier: string; tierColor: string; desc: string };
}

const SLOT_CONFIG: SlotDef[] = [
  { category: 'weapon', label: 'Weapon', icon: '⚔️', desc: 'Damage, Range' },
  { category: 'engine', label: 'Engine', icon: '🚀', desc: 'Speed, Maneuverability' },
  { category: 'shield', label: 'Shield', icon: '🛡️', desc: 'Shields, Regen' },
  { category: 'armor', label: 'Armor', icon: '🔰', desc: 'Hull, Resistance' },
  { category: 'sensor', label: 'Sensor', icon: '📡', desc: 'Vision, Accuracy' },
  { category: 'reactor', label: 'Reactor', icon: '⚡', desc: 'Power, Energy' },
  {
    category: 'special', label: 'Special', icon: '⛏️', desc: 'Mining, Utility',
    builtIn: {
      name: 'Basic Mining Array',
      tier: 'Mk0',
      tierColor: '#666666',
      desc: 'Extracts common minerals only',
    },
  },
];

const TIER_COLORS: Record<string, string> = {
  mk0: '#666666', mk1: '#888888', mk2: '#4ade80', mk3: '#4299e1', mk4: '#9333ea', prototype: '#FFD700',
};
const TIER_LABELS: Record<string, string> = {
  mk0: 'Mk0', mk1: 'Mk1', mk2: 'Mk2', mk3: 'Mk3', mk4: 'Mk4', prototype: 'Proto',
};

export class ComponentUpgradeDialog {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private dw: number;
  private dh: number;
  private visible: boolean = false;
  private currentUnitId: string | null = null;

  private purchasedComponents: (ComponentInstance & { name: string; category: string; stats: Record<string, number | string>; tier: string; description: string })[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const mobile = isMobile(scene);
    this.dw = mobile ? 340 : 652;
    this.dh = mobile ? 540 : 620;
    const { width, height } = scene.cameras.main;
    this.container = scene.add.container(width / 2 - this.dw / 2, height / 2 - this.dh / 2);
    this.container.setDepth(2000);
    this.container.setVisible(false);

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    EventBus.on('unit_components_updated', this.onLegacyComponentsUpdate, this);
    EventBus.on('components_changed', this.onComponentsChanged, this);
  }

  private onLegacyComponentsUpdate(_data: { unitId: string; components: any[] }): void {
    if (this.visible && this.currentUnitId) {
      this.buildDialog();
    }
  }

  private async onComponentsChanged(): Promise<void> {
    if (this.visible && this.currentUnitId) {
      await this.loadPurchasedComponents();
      this.buildDialog();
    }
  }

  private async loadPurchasedComponents(): Promise<void> {
    try {
      const monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);
      const [owned, defs] = await Promise.all([
        monetization.fetchPlayerComponents(),
        monetization.fetchAvailableComponents(),
      ]);

      const defMap = new Map<string, ComponentDefinition>();
      for (const d of defs) defMap.set(d.id, d);

      this.purchasedComponents = owned
        .filter(inst => inst.shipId === this.currentUnitId && inst.status === 'equipped')
        .map(inst => {
          const def = defMap.get(inst.definitionId);
          return {
            ...inst,
            name: def?.name || 'Unknown Component',
            category: def?.category || 'special',
            stats: (def?.stats || {}) as Record<string, number | string>,
            tier: def?.tier || inst.currentTier || 'mk1',
            description: def?.description || '',
          };
        });
    } catch (err) {
      console.error('[ComponentUpgradeDialog] Failed to load purchased components:', err);
      this.purchasedComponents = [];
    }
  }

  private async buildDialog(): Promise<void> {
    this.container.removeAll(true);

    createDialogChrome(this.scene, this.container, this.dw, this.dh, {
      title: I18n.t('dialog.component_loadout'),
      onClose: () => this.hide(),
    });

    const slotW = this.dw - SLOT_PAD * 2;
    let y = 60;

    // Get the ship's total component slots from MMOStateManager
    let maxSlots = 2; // default
    try {
      const mmoState = ServiceLocator.get<any>(Services.MMO_STATE);
      const allUnits = mmoState?.getPlayerUnits?.() || [];
      const unit = allUnits.find((u: any) => u.id === this.currentUnitId);
      if (unit) {
        // Fetch slot count from variant data via API
        const variant = (unit as any).shipVariant;
        if (variant) {
          const resp = await fetch('/api/purchase/variants');
          if (resp.ok) {
            const data = await resp.json();
            const v = data.variants?.find((v: any) => v.id === variant);
            if (v) maxSlots = v.componentSlots || 2;
          }
        }
      }
    } catch { /* use default */ }
    // Ensure we show at least as many slots as equipped
    maxSlots = Math.max(maxSlots, this.purchasedComponents.length);

    // Show each equipped component as a filled slot
    const equippedList = [...this.purchasedComponents];

    // Show equipped components first
    for (const comp of equippedList) {
      const slotDef = SLOT_CONFIG.find(s => s.category === comp.category) || SLOT_CONFIG[0];
      const slotContainer = this.createSlotRow(slotDef, comp, slotW, y);
      this.container.add(slotContainer);
      y += SLOT_H + SLOT_GAP;
    }

    // Show remaining empty slots
    const emptySlots = maxSlots - equippedList.length;
    for (let i = 0; i < emptySlots; i++) {
      const emptyDef: SlotDef = { category: 'empty', label: `Slot ${equippedList.length + i + 1}`, icon: '[ ]', desc: 'Empty — equip from the Component Store' };
      const slotContainer = this.createSlotRow(emptyDef, undefined, slotW, y);
      this.container.add(slotContainer);
      y += SLOT_H + SLOT_GAP;
    }

    // Footer: open full store CTA
    const footerY = y + 8;
    const footerBtn = this.createFooterButton(SLOT_PAD, footerY, slotW, 36, 'OPEN COMPONENT STORE', Colors.cyan, () => {
      EventBus.emit('mmo-open-component-store');
    });
    this.container.add(footerBtn);
  }

  private createSlotRow(
    slotDef: SlotDef,
    equipped: (ComponentInstance & { name: string; category: string; stats: Record<string, number | string>; tier: string; description: string }) | undefined,
    slotW: number,
    y: number,
  ): Phaser.GameObjects.Container {
    const row = this.scene.add.container(SLOT_PAD, y);

    // Background
    const bg = this.scene.add.graphics();
    bg.fillStyle(Colors.bgCard, 0.8);
    bg.fillRoundedRect(0, 0, slotW, SLOT_H, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.06);
    bg.strokeRoundedRect(0, 0, slotW, SLOT_H, Radius.sm);
    row.add(bg);

    // Category icon
    const icon = this.scene.add.text(14, SLOT_H / 2, slotDef.icon, { fontSize: '20px' });
    icon.setOrigin(0, 0.5);
    row.add(icon);

    // Category label
    const catLabel = this.scene.add.text(44, 10, slotDef.label.toUpperCase(), {
      fontSize: '10px', fontStyle: '900', color: '#99aabb',
      fontFamily: Fonts.heading, letterSpacing: 1,
    });
    row.add(catLabel);

    const hasEquipped = !!equipped;
    const hasBuiltIn = !hasEquipped && !!slotDef.builtIn;

    if (hasEquipped) {
      // Show equipped purchased component
      const tierKey = equipped.tier;
      const tierLabel = TIER_LABELS[tierKey] || tierKey;
      const tierColor = TIER_COLORS[tierKey] || '#888888';

      // Component name
      const nameText = this.scene.add.text(44, 28, equipped.name, {
        fontSize: FontSize.sm, fontStyle: 'bold', color: '#ffffff', fontFamily: Fonts.body,
      });
      row.add(nameText);

      // Tier badge
      const badge = this.scene.add.text(44, 46, tierLabel, {
        fontSize: '9px', fontStyle: '900', color: tierColor,
        fontFamily: Fonts.heading, letterSpacing: 1,
      });
      row.add(badge);

      // Stat summary (same style for all component types)
      const cond = equipped.conditionPercent ?? 100;
      const statParts: string[] = [];
      for (const [key, val] of Object.entries(equipped.stats)) {
        if (key === 'maxMineralRarity') {
          statParts.push(`Rarity: ${String(val)}`);
        } else if (typeof val === 'number') {
          const pct = `+${Math.round(val * 100)}%`;
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          statParts.push(`${label}: ${pct}`);
        }
      }
      if (statParts.length > 0) {
        const statsText = this.scene.add.text(badge.x + badge.width + 14, 47, statParts.join('  ·  '), {
          fontSize: '9px', color: '#66aa88', fontFamily: Fonts.body,
        });
        row.add(statsText);
      }

      // Right-side buttons: REPAIR with condition % (if damaged) + UPGRADE
      if (cond < 100) {
        const repairColor = cond > 50 ? Colors.green : cond > 25 ? Colors.orange : Colors.red;
        this.addSlotButton(row, slotW, `REPAIR ${cond}%`, repairColor, () => {
          this.showRepairConfirm(equipped);
        }, 114);
        this.addSlotButton(row, slotW, 'UPGRADE', Colors.green, () => {
          EventBus.emit('mmo-open-component-store', { category: slotDef.category });
        }, 0);
      } else {
        this.addSlotButton(row, slotW, 'UPGRADE', Colors.green, () => {
          EventBus.emit('mmo-open-component-store', { category: slotDef.category });
        });
      }

    } else if (hasBuiltIn) {
      // Show virtual built-in component
      const bi = slotDef.builtIn!;
      const nameText = this.scene.add.text(44, 28, bi.name, {
        fontSize: FontSize.sm, fontStyle: 'bold', color: '#7788aa', fontFamily: Fonts.body,
      });
      row.add(nameText);

      const tierBadge = this.scene.add.text(44, 46, bi.tier, {
        fontSize: '9px', fontStyle: '900', color: bi.tierColor,
        fontFamily: Fonts.heading, letterSpacing: 1,
      });
      row.add(tierBadge);

      const descText = this.scene.add.text(tierBadge.x + tierBadge.width + 14, 47, bi.desc, {
        fontSize: '9px', color: '#556677', fontFamily: Fonts.body, fontStyle: 'italic',
      });
      row.add(descText);

      // "BUY UPGRADE" button
      this.addSlotButton(row, slotW, 'BUY UPGRADE', Colors.cyan, () => {
        EventBus.emit('mmo-open-component-store', { category: slotDef.category });
      });

    } else {
      // Empty slot
      const emptyText = this.scene.add.text(44, 28, 'No component installed', {
        fontSize: FontSize.sm, color: '#445566', fontFamily: Fonts.body, fontStyle: 'italic',
      });
      row.add(emptyText);

      const subText = this.scene.add.text(44, 46, slotDef.desc, {
        fontSize: '9px', color: '#334455', fontFamily: Fonts.body,
      });
      row.add(subText);

      // "BROWSE PARTS" button
      this.addSlotButton(row, slotW, 'BROWSE PARTS', Colors.cyanDim, () => {
        EventBus.emit('mmo-open-component-store', { category: slotDef.category });
      });
    }

    return row;
  }

  private addSlotButton(
    row: Phaser.GameObjects.Container,
    slotW: number,
    label: string,
    color: number,
    onClick: () => void,
    extraRightOffset: number = 0,
  ): void {
    const btnW = 100;
    const btnH = 26;
    const btnX = slotW - btnW - 12 - extraRightOffset;
    const btnY = (SLOT_H - btnH) / 2;
    const colorCSS = '#' + color.toString(16).padStart(6, '0');

    const btnBg = this.scene.add.graphics();
    const drawBtn = (hover: boolean) => {
      btnBg.clear();
      btnBg.lineStyle(1.5, color, hover ? 0.8 : 0.4);
      btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 6);
      btnBg.fillStyle(color, hover ? 0.15 : 0.05);
      btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 6);
    };
    drawBtn(false);
    row.add(btnBg);

    const btnText = this.scene.add.text(btnX + btnW / 2, btnY + btnH / 2, label, {
      fontSize: '9px', fontStyle: '900', color: colorCSS,
      fontFamily: Fonts.heading, letterSpacing: 1,
    });
    btnText.setOrigin(0.5);
    row.add(btnText);

    const hitZone = this.scene.add.zone(btnX, btnY, btnW, btnH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      drawBtn(true);
      btnText.setColor('#ffffff');
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawBtn(false);
      btnText.setColor(colorCSS);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', onClick);
    row.add(hitZone);
  }

  private createFooterButton(
    x: number, y: number, w: number, h: number,
    label: string, color: number, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const colorCSS = '#' + color.toString(16).padStart(6, '0');

    const bg = this.scene.add.graphics();
    const drawBg = (hover: boolean) => {
      bg.clear();
      bg.lineStyle(1.5, color, hover ? 0.6 : 0.3);
      bg.strokeRoundedRect(0, 0, w, h, 8);
      bg.fillStyle(color, hover ? 0.12 : 0.04);
      bg.fillRoundedRect(0, 0, w, h, 8);
    };
    drawBg(false);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: '11px', fontStyle: '900', color: colorCSS,
      fontFamily: Fonts.heading, letterSpacing: 2,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => { drawBg(true); text.setColor('#ffffff'); this.scene.input.setDefaultCursor('pointer'); });
    hitZone.on('pointerout', () => { drawBg(false); text.setColor(colorCSS); this.scene.input.setDefaultCursor('default'); });
    hitZone.on('pointerdown', onClick);
    btn.add(hitZone);

    return btn;
  }

  private async showRepairConfirm(
    equipped: ComponentInstance & { name: string; category: string; stats: Record<string, number | string>; tier: string; description: string },
  ): Promise<void> {
    const monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);
    const estimate = await monetization.getRepairEstimate(equipped.id);

    // Rebuild dialog with repair overlay
    this.container.removeAll(true);
    createDialogChrome(this.scene, this.container, this.dw, this.dh, {
      title: I18n.t('dialog.repair_component'),
      onClose: () => { this.buildDialog(); },
    });

    let y = 65;

    // Component info
    const tierLabel = TIER_LABELS[equipped.tier] || equipped.tier;
    const condPct = equipped.conditionPercent ?? 100;

    const nameText = this.scene.add.text(SLOT_PAD, y, `${equipped.name}  [${tierLabel}]`, {
      fontSize: FontSize.md, fontStyle: 'bold', color: '#ffffff', fontFamily: Fonts.body,
    });
    this.container.add(nameText);
    y += 28;

    // Condition bar
    const barX = SLOT_PAD;
    const barW = this.dw - SLOT_PAD * 2;
    const barH = 14;
    const condColor = condPct > 50 ? 0x44ff44 : condPct > 25 ? 0xffaa00 : 0xff4444;

    const condBg = this.scene.add.graphics();
    condBg.fillStyle(0x112233, 1);
    condBg.fillRoundedRect(barX, y, barW, barH, 4);
    this.container.add(condBg);

    const fillW = barW * (condPct / 100);
    const condFill = this.scene.add.graphics();
    condFill.fillStyle(condColor, 1);
    condFill.fillRoundedRect(barX, y, fillW, barH, 4);
    this.container.add(condFill);

    // Compute achievable target based on mineral availability
    let targetPct = 100;
    if (estimate.success && estimate.costs) {
      let repairFraction = 1.0;
      for (const { needed, available } of Object.values(estimate.costs)) {
        if (needed > 0 && available < needed) {
          repairFraction = Math.min(repairFraction, available / needed);
        }
      }
      targetPct = Math.min(100, condPct + Math.round((100 - condPct) * repairFraction));
    }

    const condLabel = this.scene.add.text(barX + barW / 2, y + barH / 2,
      targetPct >= 100 ? `${condPct}% → 100%` : `${condPct}% → ${targetPct}%`, {
        fontSize: '11px', fontStyle: '900', color: '#ffffff', fontFamily: Fonts.heading,
      });
    condLabel.setOrigin(0.5);
    this.container.add(condLabel);
    y += barH + 20;

    if (!estimate.success || !estimate.costs) {
      const errText = this.scene.add.text(SLOT_PAD, y, estimate.error || 'Could not fetch repair costs', {
        fontSize: FontSize.sm, color: '#ff6666', fontFamily: Fonts.body,
      });
      this.container.add(errText);
      y += 30;
    } else {
      // Mineral costs table
      const headerText = this.scene.add.text(SLOT_PAD, y, 'MINERAL COSTS', {
        fontSize: '10px', fontStyle: '900', color: '#99aabb',
        fontFamily: Fonts.heading, letterSpacing: 1,
      });
      this.container.add(headerText);
      y += 22;

      let canFullRepair = true;
      for (const [mineralId, { needed, available }] of Object.entries(estimate.costs)) {
        const mineralName = MINERAL_LABELS[mineralId] || mineralId.replace('min_', '');
        const hasEnough = available >= needed;
        if (!hasEnough && needed > 0) canFullRepair = false;

        const rowBg = this.scene.add.graphics();
        rowBg.fillStyle(hasEnough ? 0x112233 : 0x331111, 0.6);
        rowBg.fillRoundedRect(SLOT_PAD, y, barW, 28, 4);
        this.container.add(rowBg);

        const nameLabel = this.scene.add.text(SLOT_PAD + 12, y + 14, mineralName, {
          fontSize: FontSize.sm, color: '#ccddee', fontFamily: Fonts.body, fontStyle: 'bold',
        });
        nameLabel.setOrigin(0, 0.5);
        this.container.add(nameLabel);

        const amountLabel = this.scene.add.text(
          SLOT_PAD + barW - 12, y + 14,
          `${needed} needed  ·  ${Math.floor(available)} available`,
          {
            fontSize: FontSize.sm,
            color: hasEnough ? '#44ff44' : '#ff6666',
            fontFamily: Fonts.body,
          },
        );
        amountLabel.setOrigin(1, 0.5);
        this.container.add(amountLabel);
        y += 32;
      }

      y += 12;

      // Partial repair note
      if (!canFullRepair) {
        const partialNote = this.scene.add.text(SLOT_PAD, y, 'Not enough minerals for full repair — partial repair will be applied.', {
          fontSize: '10px', color: '#ffaa44', fontFamily: Fonts.body, fontStyle: 'italic',
          wordWrap: { width: barW },
        });
        this.container.add(partialNote);
        y += 28;
      }

      // REPAIR button
      const repairBtn = this.createFooterButton(SLOT_PAD, y, barW, 40, 'REPAIR NOW', Colors.orange, async () => {
        const result = await monetization.repairComponent(equipped.id);
        if (result.success) {
          EventBus.emit('components_changed');

          // Update unit sprite condition so CommandDock warning dot clears
          if (this.currentUnitId && result.newCondition !== undefined) {
            EventBus.emit('component_condition_updated', {
              unitId: this.currentUnitId,
              conditionPercent: result.newCondition,
            });
          }

          // Success toast
          const spent = result.mineralsSpent || {};
          const spentParts = Object.entries(spent)
            .map(([id, amt]) => `${amt} ${MINERAL_LABELS[id] || id.replace('min_', '')}`);
          const toastMsg = spentParts.length > 0
            ? `Repaired to ${result.newCondition}% (used ${spentParts.join(', ')})`
            : `Repaired to ${result.newCondition}%`;
          EventBus.emit('show-notification', toastMsg);

          await this.loadPurchasedComponents();
          this.buildDialog();
        } else {
          EventBus.emit('show-notification', `Repair failed: ${result.error}`);
        }
      });
      this.container.add(repairBtn);
      y += 50;

      // BACK button
      const backBtn = this.createFooterButton(SLOT_PAD, y, barW, 34, 'BACK', Colors.cyanDim, () => {
        this.buildDialog();
      });
      this.container.add(backBtn);
    }
  }

  public async show(unitId: string): Promise<void> {
    this.currentUnitId = unitId;
    this.visible = true;

    this.container.removeAll(true);
    this.container.setVisible(true);
    this.container.setAlpha(1);

    // Build a loading state first
    createDialogChrome(this.scene, this.container, this.dw, this.dh, {
      title: I18n.t('dialog.component_loadout'),
      onClose: () => this.hide(),
    });
    const loadingText = this.scene.add.text(this.dw / 2, this.dh / 2, 'Loading...', {
      fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.body,
    });
    loadingText.setOrigin(0.5);
    this.container.add(loadingText);

    // Fetch purchased component data
    await this.loadPurchasedComponents();
    this.buildDialog();

    EventBus.on('close-topmost-dialog', this.hide, this);
  }

  public hide(): void {
    EventBus.off('close-topmost-dialog', this.hide, this);
    this.visible = false;
    this.currentUnitId = null;
    this.scene.input.setDefaultCursor('default');

    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public destroy(): void {
    EventBus.off('unit_components_updated', this.onLegacyComponentsUpdate, this);
    EventBus.off('components_changed', this.onComponentsChanged, this);
    this.container.destroy();
  }
}
